import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Sparkles, X, Check, ChefHat } from 'lucide-react';
import { PREFERENCE_MODES, DEFAULT_PREFERENCE_MODE } from '@/utils/cuisineSimilarity';
import { CUISINE_OPTIONS } from '@/utils/culturalOrigins';

const cuisineOptions = CUISINE_OPTIONS;

const dietaryPreferences = [
  { value: 'vegetarian', label: 'Vegetarian', icon: '🥬' },
  { value: 'vegan', label: 'Vegan', icon: '🌱' },
  { value: 'pescatarian', label: 'Pescatarian', icon: '🐟' },
  { value: 'halal', label: 'Halal', icon: '☪️' },
  { value: 'kosher', label: 'Kosher', icon: '✡️' },
  { value: 'gluten_free', label: 'Gluten-Free', icon: '🌾' },
  { value: 'dairy_free', label: 'Dairy-Free', icon: '🥛' },
  { value: 'low_carb', label: 'Low-Carb', icon: '🥩' },
  { value: 'keto', label: 'Keto', icon: '🥑' },
  { value: 'paleo', label: 'Paleo', icon: '🦴' }
];

const MealPreferencesModal = ({ open, onClose, initialPreferences = {}, initialMode = DEFAULT_PREFERENCE_MODE, onSave }) => {
  const [preferences, setPreferences] = useState({
    cuisines: [],
    dietary: [],
    allergies: [],
    dislikes: [],
    ...initialPreferences
  });

  const [preferenceMode, setPreferenceMode] = useState(initialMode);
  const [allergyInput, setAllergyInput] = useState('');
  const [dislikeInput, setDislikeInput] = useState('');

  useEffect(() => {
    if (initialPreferences && Object.keys(initialPreferences).length > 0) {
      setPreferences({
        cuisines: initialPreferences.cuisines || [],
        dietary: initialPreferences.dietary || [],
        allergies: initialPreferences.allergies || [],
        dislikes: initialPreferences.dislikes || []
      });
    }
    setPreferenceMode(initialMode || DEFAULT_PREFERENCE_MODE);
  }, [initialPreferences, initialMode]);

  const toggleCuisine = (value) => {
    setPreferences(prev => ({
      ...prev,
      cuisines: prev.cuisines.includes(value)
        ? prev.cuisines.filter(c => c !== value)
        : [...prev.cuisines, value]
    }));
  };

  const toggleDietary = (value) => {
    setPreferences(prev => ({
      ...prev,
      dietary: prev.dietary.includes(value)
        ? prev.dietary.filter(d => d !== value)
        : [...prev.dietary, value]
    }));
  };

  const addAllergy = () => {
    if (allergyInput.trim()) {
      setPreferences(prev => ({
        ...prev,
        allergies: [...prev.allergies, allergyInput.trim()]
      }));
      setAllergyInput('');
    }
  };

  const removeAllergy = (item) => {
    setPreferences(prev => ({
      ...prev,
      allergies: prev.allergies.filter(a => a !== item)
    }));
  };

  const addDislike = () => {
    if (dislikeInput.trim()) {
      setPreferences(prev => ({
        ...prev,
        dislikes: [...prev.dislikes, dislikeInput.trim()]
      }));
      setDislikeInput('');
    }
  };

  const removeDislike = (item) => {
    setPreferences(prev => ({
      ...prev,
      dislikes: prev.dislikes.filter(d => d !== item)
    }));
  };

  const handleSave = () => {
    onSave(preferences, preferenceMode);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ChefHat className="h-5 w-5 text-purple-600" />
            Customize Your Meal Plan
          </DialogTitle>
          <DialogDescription>
            Personalize your nutrition plan based on your cultural preferences, dietary needs, and taste
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preferred Cuisines */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">
              Preferred Cuisines
            </Label>
            <p className="text-xs text-gray-600 mb-3">
              Select all cuisines you enjoy (recommended: 2-3 for variety)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {cuisineOptions.map((cuisine) => (
                <button
                  key={cuisine.value}
                  onClick={() => toggleCuisine(cuisine.value)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    preferences.cuisines.includes(cuisine.value)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{cuisine.emoji}</span>
                    <span className="text-sm font-medium">{cuisine.label}</span>
                  </div>
                  {preferences.cuisines.includes(cuisine.value) && (
                    <Check className="h-4 w-4 text-purple-600 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Meal Preference Mode - NEW */}
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-100">
            <Label className="text-sm font-semibold mb-2 block flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" />
              How should we recommend meals?
            </Label>
            <p className="text-xs text-gray-600 mb-4">
              Choose how culturally focused you want your meal suggestions to be
            </p>
            
            <div className="space-y-2">
              {Object.entries(PREFERENCE_MODES).map(([key, mode]) => (
                <button
                  key={key}
                  onClick={() => setPreferenceMode(key)}
                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                    preferenceMode === key
                      ? 'border-purple-500 bg-white shadow-sm'
                      : 'border-gray-200 hover:border-purple-300 bg-white/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl mt-0.5">{mode.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm">{mode.name}</span>
                        {preferenceMode === key && (
                          <Check className="h-4 w-4 text-purple-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{mode.description}</p>
                      
                      {/* Distribution visualization */}
                      <div className="flex items-center gap-1 mt-2">
                        <div 
                          className="h-1.5 bg-purple-500 rounded-full" 
                          style={{ width: `${mode.distribution.preferred * 100}%` }}
                          title="Your cuisines"
                        />
                        <div 
                          className="h-1.5 bg-teal-400 rounded-full" 
                          style={{ width: `${mode.distribution.similar * 100}%` }}
                          title="Similar cuisines"
                        />
                        <div 
                          className="h-1.5 bg-amber-400 rounded-full" 
                          style={{ width: `${mode.distribution.explore * 100}%` }}
                          title="Explore new"
                        />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 bg-purple-500 rounded-full" />
                <span>Your cuisines</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 bg-teal-400 rounded-full" />
                <span>Similar</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="h-2 w-2 bg-amber-400 rounded-full" />
                <span>Explore</span>
              </div>
            </div>
          </div>

          {/* Dietary Preferences */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">
              Dietary Preferences & Restrictions
            </Label>
            <p className="text-xs text-gray-600 mb-3">
              Select any dietary guidelines you follow
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {dietaryPreferences.map((diet) => (
                <button
                  key={diet.value}
                  onClick={() => toggleDietary(diet.value)}
                  className={`p-3 rounded-lg border-2 transition-all text-left ${
                    preferences.dietary.includes(diet.value)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{diet.icon}</span>
                    <span className="text-sm font-medium">{diet.label}</span>
                  </div>
                  {preferences.dietary.includes(diet.value) && (
                    <Check className="h-4 w-4 text-green-600 ml-auto" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Allergies & Intolerances */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">
              Allergies & Intolerances
            </Label>
            <p className="text-xs text-gray-600 mb-3">
              Add specific ingredients you're allergic to or can't tolerate
            </p>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="e.g., peanuts, shellfish, soy"
                value={allergyInput}
                onChange={(e) => setAllergyInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addAllergy()}
                className="flex-1"
              />
              <Button onClick={addAllergy} size="sm" variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {preferences.allergies.map((allergy, index) => (
                <Badge
                  key={index}
                  variant="destructive"
                  className="flex items-center gap-1 px-3 py-1"
                >
                  {allergy}
                  <button
                    onClick={() => removeAllergy(allergy)}
                    className="ml-1 hover:bg-red-700 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {preferences.allergies.length === 0 && (
                <p className="text-xs text-gray-500 italic">No allergies added</p>
              )}
            </div>
          </div>

          {/* Foods You Dislike */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">
              Foods You Dislike
            </Label>
            <p className="text-xs text-gray-600 mb-3">
              Add foods or ingredients you prefer to avoid
            </p>
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="e.g., mushrooms, olives, tofu"
                value={dislikeInput}
                onChange={(e) => setDislikeInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addDislike()}
                className="flex-1"
              />
              <Button onClick={addDislike} size="sm" variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {preferences.dislikes.map((dislike, index) => (
                <Badge
                  key={index}
                  variant="secondary"
                  className="flex items-center gap-1 px-3 py-1"
                >
                  {dislike}
                  <button
                    onClick={() => removeDislike(dislike)}
                    className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              {preferences.dislikes.length === 0 && (
                <p className="text-xs text-gray-500 italic">No dislikes added</p>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          <Button
            onClick={handleSave}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Save Preferences
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MealPreferencesModal;
